import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import LineChart from '../../../../shared/components/chart/line-chart/LineChart';
import CustomTooltip from '../../../../shared/components/common/tooltip/CustomTooltip';
import './DomainsDailyStatsChart.scss';
import { DomainsStatsWithShare } from '../../hooks/UseDomainsStatsShare';
import { useDomainsStatsSorted } from '../../hooks/UseDomainsStatsSorted';
import { Skeleton } from 'primereact/skeleton';
import EmptyPanel from '../../../../shared/components/empty-panel/EmptyPanel';
import FilterBox from '../../../../shared/components/filter-box/FilterBox';
import { FilterItem } from '../../../../shared/components/filter-box/models/FilterBoxModel';
import { buildBucketLabelFormatters } from '../../../../shared/helpers/date/bucketDateLabels';

interface DomainsDailyStatsChartProps {
  matrix: (string | number)[][] | null;
  selectedDomain?: string | null;
  domainsStatsWithShare?: DomainsStatsWithShare;
  isLoading: boolean;
  periodFilterItems?: FilterItem[];
  /** Active bucket period (DAILY/WEEKLY/MONTHLY) — drives X-axis/tooltip date labels. */
  period?: string | null;
}

// Constants
const SELECTORS = {
  CHART_CONTAINER: '.domains-daily-stats-chart',
  LEGEND_CONTAINER: '.line-chart__legend',
  LEGEND_ITEM: '.line-chart__legend-item',
  LEGEND_COLOR: '.line-chart__legend-color',
  TOOLTIP_CONTENT: '.line-chart-tooltip__content',
  TOOLTIP_ROW: '.line-chart-tooltip__row',
  TOOLTIP_TEXT_SPAN: '.line-chart-tooltip__text span',
} as const;

const ICON_CLASSES = {
  LEGEND: 'domains-daily-stats-chart__legend-icon',
  TOOLTIP: 'domains-daily-stats-chart__tooltip-icon',
} as const;

const FAVICON_URL = 'https://favicon.is';

export const DomainsDailyStatsChart: React.FC<DomainsDailyStatsChartProps> = ({
  matrix,
  selectedDomain,
  domainsStatsWithShare,
  isLoading,
  periodFilterItems,
  period,
}) => {
  const { t, i18n } = useTranslation();

  // Locale + period aware date labels: compact range on the axis, full range
  // (with year) in the tooltip. Raw ISO keys stay in the matrix untouched.
  const dateLabelFormatters = useMemo(
    () => buildBucketLabelFormatters(period, i18n.language),
    [period, i18n.language]
  );

  // Get sorted domains to calculate domain count map for sorting
  const sortedDomains = useDomainsStatsSorted(domainsStatsWithShare);

  // Create map of domain -> count for sorting
  const domainCountMap = useMemo(() => {
    if (!sortedDomains) return new Map<string, number>();
    return new Map(sortedDomains.map((d) => [d.domain, d.count]));
  }, [sortedDomains]);

  // Refs for DOM elements to avoid repeated queries
  const chartContainerRef = useRef<HTMLElement | null>(null);

  // Helper: Add favicon icon, or update an existing one whose domain changed. The icon's
  // background encodes the domain, so a reused DOM node (e.g. after switching group/brand) must
  // have its favicon refreshed — not just left in place — otherwise it shows the stale domain.
  const syncFaviconIcon = useCallback(
    (existingIcon: Element | null, parent: Element, textNode: Node, domain: string, iconClass: string): void => {
      if (existingIcon instanceof HTMLElement) {
        if (existingIcon.dataset.domain !== domain) {
          existingIcon.style.backgroundImage = `url("${FAVICON_URL}/${domain}")`;
          existingIcon.dataset.domain = domain;
        }
        return;
      }

      const icon = document.createElement('span');
      icon.className = iconClass;
      icon.style.backgroundImage = `url("${FAVICON_URL}/${domain}")`;
      icon.dataset.domain = domain;

      const insertTarget = textNode.nodeType === Node.TEXT_NODE ? textNode.parentElement : parent;
      insertTarget?.insertBefore(icon, textNode);
    },
    []
  );

  // Helper: Find the icon among an element's direct children (optimized - classList over querySelector)
  const getDirectIcon = useCallback((element: Element, iconClass: string): Element | null => {
    for (let i = 0; i < element.children.length; i++) {
      if (element.children[i].classList.contains(iconClass)) {
        return element.children[i];
      }
    }
    return null;
  }, []);

  // Throttle function (moved outside to avoid recreation)
  const createThrottled = useCallback(
    <T extends (...args: unknown[]) => void>(
      func: T,
      delay: number
    ): ((...args: Parameters<T>) => void) => {
      let lastCall = 0;
      return (...args: Parameters<T>) => {
        const now = Date.now();
        if (now - lastCall >= delay) {
          lastCall = now;
          func(...args);
        }
      };
    },
    []
  );

  // Helper: Create observer with interval (memoized)
  const createDomObserver = useMemo(
    () =>
      (target: Node, callback: () => void, options: { interval?: number; throttle?: number } = {}) => {
        const { interval = 500, throttle: throttleDelay = 200 } = options;
        const throttledCallback =
          throttleDelay > 0 ? createThrottled(callback, throttleDelay) : callback;

        const observer = new MutationObserver(throttledCallback);
        observer.observe(target, {
          childList: true,
          subtree: true,
          attributes: false,
        });

        const intervalId = setInterval(throttledCallback, interval);

        return () => {
          observer.disconnect();
          clearInterval(intervalId);
        };
      },
    [createThrottled]
  );

  // Helper functions for legend processing (memoized to avoid recreation)
  const getLegendDomain = useCallback((item: Element): string | null => {
    const colorSpan = item.querySelector(SELECTORS.LEGEND_COLOR);
    if (!colorSpan) return item.textContent?.trim() || null;

    // Get all text content after the color span, excluding icon elements
    let text = '';
    let current: Node | null = colorSpan.nextSibling;
    while (current) {
      if (current.nodeType === Node.TEXT_NODE) {
        text += current.textContent || '';
      } else if (current.nodeType === Node.ELEMENT_NODE) {
        const el = current as Element;
        if (!el.classList.contains(ICON_CLASSES.LEGEND)) {
          text += el.textContent || '';
        }
      }
      current = current.nextSibling;
    }

    return text.trim() || item.textContent?.trim() || null;
  }, []);

  const getLegendTextNode = useCallback((item: Element): Node | null => {
    const colorSpan = item.querySelector(SELECTORS.LEGEND_COLOR);
    if (!colorSpan) return null;

    // Find the first text node or element node after colorSpan (excluding icons)
    let current: Node | null = colorSpan.nextSibling;
    while (current) {
      if (current.nodeType === Node.TEXT_NODE) {
        if (current.textContent?.trim()) return current;
      } else if (current.nodeType === Node.ELEMENT_NODE) {
        const el = current as Element;
        if (!el.classList.contains(ICON_CLASSES.LEGEND) && el.textContent?.trim()) {
          return current;
        }
      }
      current = current.nextSibling;
    }

    return null;
  }, []);

  // Process legend: add icons and sort by count
  const processLegend = useCallback(() => {
    if (!sortedDomains?.length) return;

    const legendContainer = chartContainerRef.current?.querySelector(SELECTORS.LEGEND_CONTAINER);
    if (!legendContainer) return;

    const legendItems = Array.from(legendContainer.querySelectorAll(SELECTORS.LEGEND_ITEM));
    if (!legendItems.length) return;

    const isProcessed = legendContainer.hasAttribute('data-processed');

    // If already processed, add missing icons and refresh any whose domain changed
    if (isProcessed) {
      legendItems.forEach((item) => {
        const textNode = getLegendTextNode(item);
        if (!textNode) return;

        const domain = getLegendDomain(item);
        if (!domain) return;

        const existingIcon = getDirectIcon(item, ICON_CLASSES.LEGEND);
        syncFaviconIcon(existingIcon, item, textNode, domain, ICON_CLASSES.LEGEND);
      });
      return;
    }

    // Extract domain and count, then sort
    const itemsWithData = legendItems
      .map((item) => {
        const domain = getLegendDomain(item);
        return {
          item,
          domain: domain || '',
          count: domain ? domainCountMap.get(domain) || 0 : 0,
        };
      })
      .sort((a, b) => b.count - a.count);

    // Add/refresh icons and reorder in one pass
    itemsWithData.forEach(({ item, domain }) => {
      const textNode = getLegendTextNode(item);
      if (textNode && domain) {
        const existingIcon = getDirectIcon(item, ICON_CLASSES.LEGEND);
        syncFaviconIcon(existingIcon, item, textNode, domain, ICON_CLASSES.LEGEND);
      }
      legendContainer.appendChild(item);
    });

    legendContainer.setAttribute('data-processed', 'true');
  }, [sortedDomains, domainCountMap, getLegendDomain, getLegendTextNode, syncFaviconIcon, getDirectIcon]);

  // Effect for legend processing
  useEffect(() => {
    chartContainerRef.current = document.querySelector(SELECTORS.CHART_CONTAINER) as HTMLElement | null;
    if (!chartContainerRef.current) return;

    // Clear processed flag when data changes
    const legendContainer = chartContainerRef.current.querySelector(SELECTORS.LEGEND_CONTAINER);
    legendContainer?.removeAttribute('data-processed');

    processLegend();

    return createDomObserver(chartContainerRef.current, processLegend, {
      interval: 500,
      throttle: 200,
    });
  }, [matrix, sortedDomains, processLegend, createDomObserver]);

  // Helper: Parse value from formatted string (e.g., "1,234" -> 1234)
  const parseValue = useCallback((valueText: string | null): number => {
    if (!valueText) return 0;
    const cleaned = valueText.replace(/[,\s]/g, '');
    const parsed = parseInt(cleaned, 10);
    return isNaN(parsed) ? 0 : parsed;
  }, []);

  // Process tooltip: add icons and sort by count
  const processTooltip = useCallback(() => {
    const tooltip = document.querySelector(SELECTORS.TOOLTIP_CONTENT) as HTMLElement | null;
    if (!tooltip) return;

    const isProcessed = tooltip.hasAttribute('data-processed');

    // If already processed, add missing icons and refresh any whose domain changed
    if (isProcessed) {
      const textElements = tooltip.querySelectorAll(SELECTORS.TOOLTIP_TEXT_SPAN);
      textElements.forEach((span) => {
        const domain = span.textContent?.trim();
        const parent = span.parentElement;
        if (!domain || !parent) return;

        const prev = span.previousElementSibling;
        const existingIcon = prev?.classList.contains(ICON_CLASSES.TOOLTIP) ? prev : null;
        syncFaviconIcon(existingIcon, parent, span, domain, ICON_CLASSES.TOOLTIP);
      });
      return;
    }

    // Hide tooltip immediately
    tooltip.style.opacity = '0';

    // Get all tooltip rows
    const rows = Array.from(tooltip.querySelectorAll(SELECTORS.TOOLTIP_ROW));
    if (!rows.length) return;

    // Extract domain and value, then sort
    const rowsWithData = rows
      .map((row) => {
        const domainSpan = row.querySelector(SELECTORS.TOOLTIP_TEXT_SPAN);
        const valueElement = row.querySelector('strong');
        const domain = domainSpan?.textContent?.trim() || '';
        const value = parseValue(valueElement?.textContent?.trim() || null);
        return { row, domain, value };
      })
      .sort((a, b) => b.value - a.value);

    // Reorder rows (batch DOM operations)
    rowsWithData.forEach(({ row }) => tooltip.appendChild(row));

    // Add/refresh icons
    const textElements = tooltip.querySelectorAll(SELECTORS.TOOLTIP_TEXT_SPAN);
    textElements.forEach((span) => {
      const domain = span.textContent?.trim();
      const parent = span.parentElement;
      if (!domain || !parent) return;

      const prev = span.previousElementSibling;
      const existingIcon = prev?.classList.contains(ICON_CLASSES.TOOLTIP) ? prev : null;
      syncFaviconIcon(existingIcon, parent, span, domain, ICON_CLASSES.TOOLTIP);
    });

    // Mark as processed and show
    tooltip.setAttribute('data-processed', 'true');
    requestAnimationFrame(() => {
      tooltip.style.opacity = '1';
    });
  }, [parseValue, syncFaviconIcon]);

  // Effect for tooltip processing
  useEffect(() => {
    const tooltip = document.querySelector(SELECTORS.TOOLTIP_CONTENT) as HTMLElement | null;
    if (tooltip) {
      tooltip.removeAttribute('data-processed');
      tooltip.style.opacity = '0';
    }

    const checkAndProcess = () => {
      const tooltip = document.querySelector(SELECTORS.TOOLTIP_CONTENT) as HTMLElement | null;
      if (tooltip && !tooltip.hasAttribute('data-processed')) {
        processTooltip();
      }
    };

    const observer = new MutationObserver(checkAndProcess);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: false,
    });

    const interval = setInterval(checkAndProcess, 50);

    return () => {
      observer.disconnect();
      clearInterval(interval);
    };
  }, [matrix, processTooltip]);

  return (
    <div className="stats__panel domains-daily-stats-chart__panel">
      <div className="page-box__title-action">
        <div className="page-box__title page-box__title--flex">
          {t('citationTrends')}
          <CustomTooltip tooltipText="citationTrendsTooltip" addMargin={false} />
        </div>
        {periodFilterItems && periodFilterItems.length > 0 && <FilterBox items={periodFilterItems} />}
      </div>
      <div className="stats-box stats-box--line-chart">
        {isLoading ? (
          <div className="stats-box__content domains-daily-stats-chart stats-box--chart">
            <Skeleton className="skeleton--chart">
              <span />
            </Skeleton>
          </div>
        ) : !matrix || matrix.length <= 1 ? (
          <div className="stats-box__content stats-box__content--empty">
            <EmptyPanel />
          </div>
        ) : (
          <div className="stats-box__content domains-daily-stats-chart ">
            <LineChart
              firstData={matrix}
              headers={matrix[0]?.slice(1) as string[]}
              selectedBrand={selectedDomain || undefined}
              legendType="FLEX_START"
              autoFitYAxis={true}
              minValue={0}
              smooth={true}
              dateAxisFormatter={dateLabelFormatters.axis}
              dateTooltipFormatter={dateLabelFormatters.tooltip}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default DomainsDailyStatsChart;
